import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface MobileMapProps {
  center?: [number, number];
  zoom?: number;
  userLat?: number | null;
  userLng?: number | null;
  className?: string;
  children?: (map: L.Map) => React.ReactNode;
  onMapReady?: (map: L.Map) => void;
}

function createUserDot(): L.DivIcon {
  return L.divIcon({
    className: 'user-location-dot',
    html: `
      <div style="position: relative; width: 20px; height: 20px;">
        <div style="
          position: absolute; inset: -6px; border-radius: 50%;
          background: rgba(59, 130, 246, 0.15);
          animation: pulse-ring 2s ease-out infinite;
        "></div>
        <div style="
          width: 20px; height: 20px; border-radius: 50%;
          background: #3b82f6; border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        "></div>
      </div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

export default function MobileMap({
  center,
  zoom = 15,
  userLat,
  userLng,
  className = '',
  children,
  onMapReady,
}: MobileMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initialCenter: [number, number] = center ??
      (userLat && userLng ? [userLat, userLng] : [-23.5505, -46.6333]);

    const map = L.map(containerRef.current, {
      center: initialCenter,
      zoom,
      zoomControl: false, // Touch-optimized: no zoom buttons
      attributionControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;
    onMapReady?.(map);

    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapRef.current = null;
      userMarkerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update user position dot
  useEffect(() => {
    if (!mapRef.current || !userLat || !userLng) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng([userLat, userLng]);
    } else {
      userMarkerRef.current = L.marker([userLat, userLng], {
        icon: createUserDot(),
        interactive: false,
        zIndexOffset: 1000,
      }).addTo(mapRef.current);
    }
  }, [userLat, userLng]);

  return (
    <div className={`relative ${className}`}>
      <div ref={containerRef} className="h-full w-full rounded-xl" />
      {/* Inject global CSS for pulse animation */}
      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      `}</style>
      {mapRef.current && children?.(mapRef.current)}
    </div>
  );
}
